import { subYears } from "date-fns"
import { startOfTodayUTC } from "./datetime"

// Fixed at module load: this runs per row in filter predicates and grid value getters, and a
// session crossing midnight keeps one consistent cutoff.
const salesHistoryCutoff = subYears(startOfTodayUTC(), 1)

/**
 * Expected growth compares the next 52 weeks of demand against the last 52 weeks of sales, so a
 * variant needs at least a year of sales history before the comparison means anything.
 */
export const hasEnoughSalesHistory = (minSoldSince: Date | null | undefined) =>
  !!minSoldSince && minSoldSince < salesHistoryCutoff

/**
 * Expected growth as a 0–1 fraction, or `undefined` when it cannot be computed — no sales to grow
 * from, or under a year of history. Callers that display a percentage scale by 100.
 */
export function expectedGrowthFraction(
  salesLastYear: number,
  forecastNextYear: number,
  minSoldSince: Date | null | undefined,
): number | undefined {
  if (salesLastYear === 0) return undefined

  const growth = (forecastNextYear - salesLastYear) / salesLastYear
  if (!Number.isFinite(growth) || !hasEnoughSalesHistory(minSoldSince)) return undefined
  return growth
}

/**
 * Expected growth in units — the absolute difference behind {@link expectedGrowthFraction}.
 *
 * Deliberately has no sales-history requirement. The year of history {@link expectedGrowthFraction}
 * insists on exists to keep a tiny denominator from producing a meaningless ratio; a difference has
 * no denominator, so a variant three months old reports its unit change like any other. Gating it
 * would also drop those variants out of a group total while the percentage still counts them,
 * leaving a category showing negative units beside positive growth.
 *
 * Undefined only when there is nothing to report at all, so an untouched variant reads "-" rather
 * than a zero. Group totals are unaffected either way.
 */
export function expectedGrowthUnits(
  salesLastYear: number,
  forecastNextYear: number,
): number | undefined {
  if (salesLastYear === 0 && forecastNextYear === 0) return undefined
  return forecastNextYear - salesLastYear
}
